import type { DateTime } from "luxon"
import { Stack, Typography } from "@mui/material"
import { faCheck } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { CheckboxCard } from "../../../../checkbox/CheckboxCard"

import { groupRepeatDaysByMonth } from "./helpers"
import type { RepeatDeliveryDay } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    month: {
        paddingBottom: ".5rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    monthTitle: {
        textTransform: "capitalize",
    },
    muted: {
        color: theme.palette.tailwind.slate[500],
    },
    day: {
        minWidth: "6.5rem",
        minHeight: "3.25rem",
        padding: ".25rem .75rem .25rem .25rem",
        border: `1px solid ${theme.palette.tailwind.blue[500]}`,
        borderRadius: ".375rem",
        backgroundColor: theme.palette.tailwind.blue[50],
    },
    dayReadOnly: {
        minHeight: "auto",
        padding: ".25rem .75rem",
    },
    dayDeselected: {
        borderColor: theme.palette.tailwind.slate[300],
        backgroundColor: theme.palette.common.white,
        color: theme.palette.tailwind.slate[500],
    },
    dayDeselectedLabel: {
        textDecoration: "line-through",
    },
    check: {
        color: theme.palette.tailwind.blue[500],
    },
}))

type RepeatDeliveryDaysListProps = Readonly<{
    days: ReadonlyArray<RepeatDeliveryDay>
    /** Without it the days are read-only. */
    onToggle?: (date: DateTime) => void
}>

export function RepeatDeliveryDaysList({ days, onToggle }: RepeatDeliveryDaysListProps) {
    const { classes, cx } = useStyles()

    return (
        <Stack gap="1rem">
            {groupRepeatDaysByMonth(days).map(([month, monthDays]) => {
                const deselectedCount = monthDays.filter((day) => day.isDeselected).length

                return (
                    <Stack key={month} gap=".5rem">
                        <Stack direction="row" gap=".5rem" alignItems="baseline" className={classes.month}>
                            <Typography variant="subtitle2" className={classes.monthTitle}>
                                {month}
                            </Typography>
                            <LocalizedStrict
                                id="create-order-drawer-repeat-order-delivery-days-month-count"
                                vars={{ count: monthDays.length - deselectedCount }}
                            >
                                <Typography variant="caption" className={classes.muted}>
                                    {monthDays.length - deselectedCount} deliveries
                                </Typography>
                            </LocalizedStrict>
                            {deselectedCount > 0 && (
                                <LocalizedStrict
                                    id="create-order-drawer-repeat-order-delivery-days-month-deselected"
                                    vars={{ count: deselectedCount }}
                                >
                                    <Typography variant="caption" className={classes.muted}>
                                        · {deselectedCount} deselected
                                    </Typography>
                                </LocalizedStrict>
                            )}
                        </Stack>
                        <Stack direction="row" flexWrap="wrap" gap=".5rem">
                            {monthDays.map((day) => {
                                const label = (
                                    <Typography
                                        variant="body2"
                                        className={cx(day.isDeselected && classes.dayDeselectedLabel)}
                                    >
                                        {day.date.toFormat("ccc d")}
                                    </Typography>
                                )

                                return onToggle ? (
                                    <CheckboxCard
                                        key={day.date.toMillis()}
                                        checked={!day.isDeselected}
                                        disabled={day.isStart}
                                        onChange={() => onToggle(day.date)}
                                        label={label}
                                        sublabel={
                                            day.isStart && (
                                                <LocalizedStrict id="create-order-drawer-repeat-order-delivery-days-start-day">
                                                    <Typography variant="caption" className={classes.muted}>
                                                        Start date
                                                    </Typography>
                                                </LocalizedStrict>
                                            )
                                        }
                                        className={cx(classes.day, day.isDeselected && classes.dayDeselected)}
                                    />
                                ) : (
                                    <Stack
                                        key={day.date.toMillis()}
                                        direction="row"
                                        gap=".5rem"
                                        alignItems="center"
                                        className={cx(
                                            classes.day,
                                            classes.dayReadOnly,
                                            day.isDeselected && classes.dayDeselected,
                                        )}
                                    >
                                        {!day.isDeselected && (
                                            <FontAwesomeIcon icon={faCheck} size="xs" className={classes.check} />
                                        )}
                                        <Stack>
                                            {label}
                                            {day.isDeselected && (
                                                <LocalizedStrict id="confirm-order-dialog-repeat-order-day-deselected">
                                                    <Typography variant="caption">deselected</Typography>
                                                </LocalizedStrict>
                                            )}
                                        </Stack>
                                    </Stack>
                                )
                            })}
                        </Stack>
                    </Stack>
                )
            })}
        </Stack>
    )
}
